import React, { useState, useEffect, useMemo } from 'react';
import Cropper from 'react-easy-crop';
import {
  Slider,
  Button,
  Typography,
  Box,
  DialogContent,
  Dialog,
  CircularProgress,
} from '@mui/material';
import getCroppedImg from './cropImg';
import { AspectRatios } from '@t2c/ai-video-editor';
import CloseIcon from '@mui/icons-material/Close';
import { useMediaQuery } from '@mui/system';
import { useTheme } from '@mui/material/styles';

interface ImageEditorProps {
  imageFile: string;
  handleFileSelect: any;
  orientation: AspectRatios;
  resolution: string;
  fromBrollUpload?: boolean;
  open: boolean;
  onClose: () => void;
  errorMsg: string;
  height?: number;
  width?: number;
}

// ── Pure synchronous helper — never causes NaN ───────────────────────────
function resolveDimensions(
  resolution: string,
  orientation: AspectRatios,
  widthProp?: number,
  heightProp?: number
): { width: number; height: number } {
  // 1. Explicit pixel props take highest priority (passed from SORA error state)
  if (widthProp && heightProp && widthProp > 0 && heightProp > 0) {
    return { width: widthProp, height: heightProp };
  }

  // 2. Raw "WxH" resolution string (e.g. "1280x720")
  const pixelMatch = resolution?.match(/^(\d+)x(\d+)$/i);
  if (pixelMatch) {
    const w = parseInt(pixelMatch[1], 10);
    const h = parseInt(pixelMatch[2], 10);
    if (w > 0 && h > 0) return { width: w, height: h };
  }

  // 3. Named resolution + orientation
  const is169 = orientation === AspectRatios['16_9'] || (orientation as string) === '16:9';
  const is916 = orientation === AspectRatios['9_16'] || (orientation as string) === '9:16';
  const is11 = orientation === AspectRatios['1_1'] || (orientation as string) === '1:1';

  if (resolution === '720p') {
    if (is169) return { width: 1280, height: 720 };
    if (is916) return { width: 720, height: 1280 };
    if (is11) return { width: 720, height: 720 };
  }
  if (resolution === '1080p') {
    if (is169) return { width: 1920, height: 1080 };
    if (is916) return { width: 1080, height: 1920 };
    if (is11) return { width: 1024, height: 1024 };
  }

  // 4. Safe fallback — 16:9 HD, never NaN
  return { width: 1280, height: 720 };
}

const ImageEditor = ({
  imageFile,
  handleFileSelect,
  orientation,
  resolution,
  fromBrollUpload,
  open,
  onClose,
  errorMsg,
  height,
  width,
}: ImageEditorProps) => {
  const [crop, setCrop] = useState({ x: 560, y: 320 });
  const [rotation, setRotation] = useState(0);
  const [zoomControl, setZoomControl] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const zoom = zoomControl >= 0 ? 1 + zoomControl : 1 / (1 + Math.abs(zoomControl));

  const handleZoomChange = (value: number) => {
    if (value >= 1) {
      setZoomControl(value - 1);
      return;
    }

    setZoomControl(-(1 / value - 1));
  };

  // ── Dimensions derived synchronously — no useState, no async lag ────────
  // This replaces the broken useState+useEffect pattern that caused:
  //   - NaN on first render (setState is async, values were stale 0s)
  //   - Cropper receiving aspect=NaN → infinite React update loop
  // width/height props come directly from soraImageErrorState so they're
  // always the correct values on the very first render.
  const { width: expectedWidth, height: expectedHeight } = useMemo(
    () => resolveDimensions(resolution, orientation, width, height),
    [resolution, orientation, width, height]
  );

  // Reset crop/zoom/rotation only when the image or target dimensions change.
  // This fires when imageFile goes from '' → actual URL (async fetch in parent).
  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setRotation(0);
    setZoomControl(0);
  }, [imageFile, expectedWidth, expectedHeight]);

  const showCroppedImage = async () => {
    if (isUploading) return;
    setIsUploading(true);
    try {
      const croppedImage = await getCroppedImg(
        imageFile,
        croppedAreaPixels,
        rotation,
        expectedWidth,
        expectedHeight,
        fromBrollUpload
      );

      await Promise.resolve(handleFileSelect(croppedImage));
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setIsUploading(false);
    }
  };

  // Guard: don't render Cropper until we have a valid image and dimensions.
  // Rendering with aspect=NaN or imageFile='' causes Cropper to throw.
  const isReady = !!imageFile && expectedWidth > 0 && expectedHeight > 0;

  return (
    <Dialog
      PaperProps={{ sx: styles.cropWrapper }}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <Box sx={styles.closeIconWrapper}>
        <CloseIcon onClick={onClose} />
      </Box>
      {isMobile && (
        <Box sx={styles.controlsHeader}>
          <Typography sx={styles.title}>Fit the image in the box</Typography>
          <Typography sx={styles.subtitle}>{errorMsg}</Typography>
        </Box>
      )}
      <DialogContent sx={{ p: 0, overflowY: 'visible' }}>
        <Box sx={styles.editorWrapper}>
          <Box sx={styles.cropContainer}>
            {isReady ? (
              <Cropper
                image={imageFile}
                crop={crop}
                rotation={rotation}
                zoom={zoom}
                aspect={
                  expectedWidth > 0 && expectedHeight > 0 ? expectedWidth / expectedHeight : 16 / 9
                }
                restrictPosition={false}
                onCropChange={setCrop}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onZoomChange={handleZoomChange}
              />
            ) : (
              // Show a spinner while the image URL is being fetched asynchronously
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(0,0,0,0.3)',
                }}
              >
                <CircularProgress size={40} />
              </Box>
            )}
          </Box>

          <Box sx={styles.controls}>
            <Box sx={styles.controllers}>
              {!isMobile && (
                <Box sx={styles.controlsHeader}>
                  <Typography sx={styles.title}>Fit the image in the box</Typography>
                  <Typography sx={styles.subtitle}>{errorMsg}</Typography>
                </Box>
              )}

              <Box sx={styles.sliderContainer}>
                <Box sx={styles.zoomRotate}>
                  <Typography sx={styles.sliderLabel}>Zoom</Typography>
                  <Slider
                    value={zoomControl}
                    min={-2}
                    max={2}
                    step={0.1}
                    aria-labelledby="Zoom"
                    sx={styles.slider}
                    onChange={(_, value) => setZoomControl(Array.isArray(value) ? value[0] : value)}
                    marks={[{ value: 0, label: '^' }]}
                  />
                </Box>

                <Typography sx={styles.sliderLabel}>Rotation</Typography>
                <Slider
                  value={rotation}
                  min={-180}
                  max={180}
                  step={1}
                  aria-labelledby="Rotation"
                  sx={styles.slider}
                  onChange={(_, value) => setRotation(Array.isArray(value) ? value[0] : value)}
                  marks={[{ value: 0, label: '^' }]}
                />
              </Box>
            </Box>

            <Box>
              <Button
                onClick={showCroppedImage}
                variant="contained"
                sx={styles.cropButton}
                disabled={isUploading}
              >
                {isUploading ? <CircularProgress size={20} color="inherit" /> : 'Upload Now'}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  cropWrapper: {
    display: 'flex',
    borderRadius: '16px',
    background: '#0C082A !important',
    padding: { md: '56px', xs: '40px' },
    gap: { md: '28px', xs: '24px' },
    width: '100%',
  },
  closeIconWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'absolute',
    right: { md: '23px', xs: '18px' },
    top: '22px',
    cursor: 'pointer',
  },
  editorWrapper: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    background: (theme) => theme.palette.background.secondary,
    gap: { md: '8px', sm: '24px', xs: '24px' },
    padding: '0px',
  },

  cropContainer: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    height: '485px',
  },

  controls: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: { md: 'space-between' },
    width: '100%',
    borderRadius: { xs: '16px', md: '0px 16px 16px 0px' },
    padding: '32px',
    background: (theme) => theme.palette.background.primaryV2,
    gap: { xs: '16px' },
  },

  controllers: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },

  controlsHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  title: {
    fontFamily: 'Inter',
    fontWeight: 700,
    fontSize: '25px',
    lineHeight: '32px',
    letterSpacing: '0.8px',
    verticalAlign: 'middle',
    textAlign: { md: 'left', xs: 'center' },
    color: (theme) => theme.palette.text.primary,
  },

  subtitle: {
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '20px',
    letterSpacing: '0.8px',
    textAlign: { md: 'left', xs: 'center' },
    color: (theme) => theme.palette.text.primary,
    opacity: 0.8,
  },

  sliderContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '16px',
  },

  zoomRotate: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },

  sliderLabel: {
    fontFamily: 'Inter',
    fontWeight: 700,
    fontStyle: 'Bold',
    fontSize: '18px',
    lineHeight: '22px',
    letterSpacing: '0px',
    color: (theme) => theme.palette.text.primary,
  },

  slider: {
    flex: 1,
    px: { xs: 0, sm: 1 },
  },

  cropButton: {
    borderRadius: '6px',
    padding: '16.5px 16px 16.5px 16px',
    background: (theme) => theme.palette.activeBorder,
    color: (theme) => theme.palette.text.primary,
    fontFamily: 'Inter',
    fontWeight: 700,
    fontStyle: 'Bold',
    fontSize: '16px',
    lineHeight: '140%',
    letterSpacing: '0%',
    textAlign: 'center',
    boxShadow: 'none',
    width: '100%',
  },
};

export default ImageEditor;
